import DisabledFieldWrapper from 'features/Responder/components/form fields/DisabledFieldWrapper';
import RequiredFieldIndicator from 'features/Responder/components/form fields/RequiredFieldIndicator';
import React from 'react';
import { Form } from 'react-bootstrap';

const TimeSlot = ({
  startTime,
  endTime,
  scheduleDuration,
  useTourDuration,
  notAllDayTimeStart,
  setNotAllDayTimeStart,
  setUseTourDuration,
  setScheduleDuration,
  setStartTime,
  setEndTime,
  setOldTimeSlot,
  setOldScheduleDuration,
  oldTimeSlot,
  oldScheduleDuration,
  isZoneSelected,
  selectedTour,
  tours,
  getTotalDurationOfTour,
  shouldShowErrorIndicators,
  hasTimeError,
  isTargetSelected,
  startTimeField,
  endTimeField,
}) => (
  <Form.Group className="mt-4 mb-3">
    <div className="flex d-flex align-items-center mb-2">
      <div
        style={{
          flex: 1,
          minWidth: 0,
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          justifyContent: 'space-between',
        }}
      >
        <div className="flex d-flex align-items-center">
          <Form.Label className="fw-bold text-white mb-0">Time Slot</Form.Label>
          {/* Duration is now calculated from start and end times. */}
          {/* Show calculated duration */}
          <div className="text-white ms-3">
            {startTime && endTime && (
              <span>
                Duration:{' '}
                <strong>
                  {scheduleDuration === 1439
                    ? 'All Day'
                    : (() => {
                        const h = Math.floor(scheduleDuration / 60);
                        const m = scheduleDuration % 60;
                        let out = '';
                        if (h > 0) out += `${h} hr` + (h > 1 ? 's' : '');
                        if (h > 0 && m > 0) out += ' ';
                        if (m > 0) out += `${m} min` + (m > 1 ? 's' : '');
                        return out || '0 min';
                      })()}
                </strong>
              </span>
            )}
          </div>
        </div>
        <RequiredFieldIndicator
          showError={shouldShowErrorIndicators && hasTimeError}
          message="Please select a start and end time"
        />
        {/* Duration and Enforce ETA side by side */}
        <div className="d-flex align-items-center mb-0 gap-2">
          {/* Duration input removed. Duration is now calculated from start and end times. */}

          <div className="flex d-flex align-items-center">
            {!isZoneSelected && selectedTour > 0 && (
              <div className="d-flex flex-row">
                <div className="form-check">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id="enforceTourEtaCheckbox"
                    checked={useTourDuration}
                    onChange={() => {
                      if (!useTourDuration) {
                        // Checking this: uncheck All Day and set time slot based on ETA
                        const tourObj = tours[selectedTour - 1];
                        setUseTourDuration(true);
                        // Save current start/end before overwriting
                        setOldTimeSlot((prev) => {
                          // If currently using Enforce ETA, don't save the enforced ETA end time as old end time
                          const isEnforced = useTourDuration;
                          return {
                            start: startTime || '',
                            end: endTime === '23:59' || isEnforced ? prev.end || '' : endTime || '',
                          };
                        });
                        if (tourObj) {
                          const etaMinutes = Math.ceil(getTotalDurationOfTour(tourObj) / 60);
                          setScheduleDuration(etaMinutes);
                          // Use notAllDayTimeStart if available, else current startTime or '00:00'
                          const start = notAllDayTimeStart || startTime || '00:00';
                          setStartTime(start);
                          const [h, m] = start.split(':').map(Number);
                          const startDate = new Date(2000, 0, 1, h, m, 0);
                          const endDate = new Date(startDate.getTime() + etaMinutes * 60000);
                          const endH = String(endDate.getHours()).padStart(2, '0');
                          const endM = String(endDate.getMinutes()).padStart(2, '0');
                          setEndTime(`${endH}:${endM}`);
                        }
                      } else {
                        // Unchecking: restore previous time slot if available
                        setUseTourDuration(false);
                        setScheduleDuration(oldScheduleDuration);
                        setStartTime(oldTimeSlot.start || '00:00');
                        setEndTime(oldTimeSlot.end || '');
                      }
                    }}
                  />
                  <label className="form-check-label text-white" htmlFor="enforceTourEtaCheckbox">
                    Enforce Tour ETA
                    {/* Estimated tour time next to header */}
                    {!isZoneSelected &&
                      selectedTour > 0 &&
                      (() => {
                        const tourObj = tours[selectedTour - 1];
                        const durationSec = getTotalDurationOfTour(tourObj);
                        if (!tourObj) return null;
                        const h = Math.floor(durationSec / 3600);
                        const m = Math.floor((durationSec % 3600) / 60);
                        const s = durationSec % 60;
                        const parts = [];
                        if (h) parts.push(`${h}h`);
                        if (m) parts.push(`${m}m`);
                        if (s && !h && !m) parts.push(`${s}s`); // Only show seconds if < 1m
                        return (
                          <span className="text-info small ms-2">
                            Est: <strong>{parts.join(' ') || '0m'}</strong>
                          </span>
                        );
                      })()}
                  </label>
                </div>
                <div className="form-check ms-3">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id="allDayCheckbox"
                    checked={scheduleDuration === 1439 && !useTourDuration}
                    onChange={() => {
                      if (!(scheduleDuration === 1439 && !useTourDuration)) {
                        // Checking this: uncheck Enforce ETA
                        setUseTourDuration(false);
                        // Save current start/end before overwriting
                        setOldTimeSlot((prev) => {
                          const isEnforced = useTourDuration;
                          return {
                            start: startTime || prev.start || '',
                            end: isEnforced
                              ? prev.end || ''
                              : endTime === '23:59'
                                ? prev.end || ''
                                : endTime || '',
                          };
                        });
                        setNotAllDayTimeStart(startTime || '00:00');
                        setScheduleDuration(1439); // 23:59 in minutes
                        setStartTime('00:00');
                        setEndTime('23:59');
                      } else {
                        // Unchecking: both are false
                        // If oldTimeSlot is empty, clear endTime as well
                        setStartTime(oldTimeSlot.start || '00:00');
                        setEndTime(oldTimeSlot.end !== undefined ? oldTimeSlot.end : '');
                        setScheduleDuration(
                          oldTimeSlot.start && oldTimeSlot.end
                            ? (() => {
                                const [sh, sm] = (oldTimeSlot.start || '00:00')
                                  .split(':')
                                  .map(Number);
                                const [eh, em] = (oldTimeSlot.end || '00:00')
                                  .split(':')
                                  .map(Number);
                                let startMin = sh * 60 + sm;
                                let endMin = eh * 60 + em;
                                if (endMin < startMin) endMin += 24 * 60;
                                return endMin - startMin;
                              })()
                            : 0,
                        );
                      }
                    }}
                  />
                  <label className="form-check-label text-white" htmlFor="allDayCheckbox">
                    All Day
                  </label>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
    <DisabledFieldWrapper
      disabled={!isTargetSelected}
      tooltip="Select a tour or zone to enable scheduling"
      className="d-flex align-items-center"
      style={{ display: 'flex' }}
      placement="top"
      tooltipClassName="responder-tooltip"
    >
      <div
        className="flex w-50 d-flex align-items-center"
        style={{ minWidth: 0 }}
        onClick={startTimeField.handleWrapperClick}
        role="button"
        tabIndex={-1}
      >
        <Form.Control
          ref={startTimeField.inputRef}
          type="time"
          value={startTime || ''}
          onChange={startTimeField.handleChange}
          placeholder="Start"
          className={`bg-dark text-white border-secondary bg-primary${scheduleDuration === 1439 && !useTourDuration ? ' opacity-50' : ''}`}
          disabled={!isTargetSelected || (scheduleDuration === 1439 && !useTourDuration)}
        />
        <span className="mx-2 text-white">to</span>
      </div>
      <div
        className="flex flex-grow-1 d-flex align-items-center"
        style={{ minWidth: 0 }}
        onClick={endTimeField.handleWrapperClick}
        role="button"
        tabIndex={-1}
      >
        <Form.Control
          type="time"
          value={endTime || ''}
          onChange={endTimeField.handleChange}
          placeholder="End"
          className={`bg-dark text-white border-secondary bg-primary${useTourDuration || (scheduleDuration === 1439 && !useTourDuration) ? ' opacity-50' : ''}`}
          disabled={useTourDuration || (scheduleDuration === 1439 && !useTourDuration)}
          ref={endTimeField.inputRef}
        />
      </div>
    </DisabledFieldWrapper>
  </Form.Group>
);

export default TimeSlot;
